<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("schedule_settings.header") }}
        </template>

        <template v-slot:body>
            <div class="columns is-mobile is-centered">
                <div v-if="isLoading">
                    <LoadingSpinner2 class="has-added-margin" />
                </div>
                <div class="column is-full" v-else>
                    <div class="field" :class="{ 'has-error': !scheduleSettingsValidation.fightsBreakTime }">
                        <label class="label">{{ translate("schedule_settings.fights_break_time") }}</label>
                        <input v-model="fightsBreakTime" class="input" type="text" placeholder="MM:SS" />
                        <span class="help">
                            {{ translate("schedule_settings.fights_break_time_description") }}
                        </span>
                    </div>
                </div>
            </div>
        </template>

        <template v-slot:footer v-if="!isLoading">
            <div class="buttons">
                <button @click="close" class="button">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton
                    :is-loading="isSaving"
                    class="button is-primary"
                    :disabled="scheduleSettingsValidation.preventSubmit"
                    @click="confirm"
                >
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { useModal } from "../Common/useModal";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import eventsApi from "../Api/events";
    import scheduleSettingsApi from "../Api/scheduleSettings";

    const props = defineProps<{
        eventId: number;
    }>();

    const { modal, open: openModal, close: closeModal } = useModal();

    const isLoading = ref(false);
    const isSaving = ref(false);
    const fightsBreakTime = ref("");

    const scheduleSettingsValidation = computed(() => {
        const matchedFightsBreakTime = matchTimeFormat(fightsBreakTime.value);

        return {
            preventSubmit: !matchedFightsBreakTime,
            fightsBreakTime: matchedFightsBreakTime,
        };
    });

    function confirm() {
        isSaving.value = true;

        return saveScheduleSettings().finally(() => {
            isSaving.value = false;
            close();
        });
    }

    function open() {
        fightsBreakTime.value = "";
        isLoading.value = true;

        return eventsApi
            .getAdminEvent(props.eventId)
            .then(() => scheduleSettingsApi.get(props.eventId))
            .then(settings => {
                fightsBreakTime.value = settings.fightsBreakTime;
            })
            .then(() => {
                openModal();
            })
            .catch(handleError)
            .finally(() => {
                isLoading.value = false;
            });
    }

    function close() {
        closeModal();
    }

    function saveScheduleSettings() {
        return scheduleSettingsApi
            .save(props.eventId, {
                fightsBreakTime: fightsBreakTime.value,
            })
            .catch(handleError);
    }

    function matchTimeFormat(subject: string) {
        return subject.match(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/);
    }

    defineExpose({ open });
</script>
